import { useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import type { JobOutput } from '@/api/types'
import { useEscapeLayer } from '@/hooks/useEscapeLayer'
import { useScrollLock } from '@/hooks/useScrollLock'
import { Icon } from './icons'

/**
 * 结果预览遮罩（点击放大）。
 *
 * 为什么用 portal：结果网格挂在 `.card` 里，卡片有 `overflow: hidden`
 * （缩略图靠它做圆角裁切）。遮罩如果在卡片内部，会被裁在卡片范围里出不来。
 *
 * 交互按图片查看器的通用预期做，不自己发明：
 *   · 点背景 / 按 Esc 关闭
 *   · ← → 在当前结果集里切换（这是"生成 5 张要挨个看"的核心诉求）
 *   · 点图本身**不**关闭 —— 看细节时鼠标总会落在图上，点图关掉会很烦
 *   · 打开时锁 body 滚动，否则背景页面跟着滚
 */
export function ImageLightbox({
  items,
  index,
  onIndex,
  onClose,
  renderActions,
  title = '结果预览',
}: {
  items: JobOutput[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  /** 底部操作区（下载 / 存素材）由调用方提供，避免这里重复实现业务动作 */
  renderActions?: (item: JobOutput, i: number) => ReactNode
  title?: string
}) {
  const total = items.length
  const current = items[index]
  const hasPrev = index > 0
  const hasNext = index < total - 1

  const go = useCallback(
    (delta: number) => {
      const next = index + delta
      if (next >= 0 && next < total) onIndex(next)
    },
    [index, total, onIndex],
  )

  /*
   * Esc 交给浮层栈（`hooks/useEscapeLayer`），**不再自己挂一个 window 监听**：
   * 这个灯箱会叠在别的浮层之上 —— 生成抽屉里点结果图放大时抽屉还开着，
   * 各挂一个监听的结果是一次 Esc 关两层（想关灯箱，连抽屉一起没了）。
   *
   * ← → 仍归这里：翻图是灯箱独有的键，没有哪一层会跟它抢。
   */
  useEscapeLayer(onClose, !!current)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        go(-1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        go(1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go])

  // 锁背景滚动（实现见 hooks/useScrollLock —— 弹层之间不许各写一遍）
  useScrollLock(true)

  // 切换时 index 可能因结果集变化越界，兜一下，别让遮罩空着白屏
  if (!current) return null

  const src = current.url
  const poster = current.thumbUrl ?? undefined

  return createPortal(
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={title}>
      <div className="lightbox__head">
        <span className="lightbox__title">{title}</span>
        <span className="lightbox__count">
          {index + 1} / {total}
        </span>
        <div className="grow" />
        {src ? (
          <a className="lightbox__ghost" href={src} target="_blank" rel="noreferrer" title="在新窗口打开原图">
            <Icon name="link" size={15} />
          </a>
        ) : null}
        <button type="button" className="lightbox__ghost" onClick={onClose} title="关闭（Esc）">
          <Icon name="x" size={16} />
        </button>
      </div>

      {/* 背景点击关闭：用 button 承接，键盘用户也能到达 */}
      <div className="lightbox__stage" onClick={onClose} role="presentation">
        {hasPrev ? (
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => {
              e.stopPropagation()
              go(-1)
            }}
            title="上一张（←）"
          >
            <Icon name="chevronLeft" size={20} />
          </button>
        ) : null}

        {current.type === 'video' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            className="lightbox__media"
            src={src}
            poster={poster}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <img
            className="lightbox__media"
            src={src}
            alt={current.text ?? `${title} ${index + 1}`}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {hasNext ? (
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => {
              e.stopPropagation()
              go(1)
            }}
            title="下一张（→）"
          >
            <Icon name="chevronRight" size={20} />
          </button>
        ) : null}
      </div>

      <div className="lightbox__foot">
        <span className="lightbox__hint">← → 切换 · Esc 关闭 · 点背景关闭</span>
        <div className="grow" />
        {renderActions?.(current, index)}
      </div>
    </div>,
    document.body,
  )
}
